import { createClient } from '@launchdarkly/js-client-sdk';
import { mapKeys, camelCase } from 'lodash';

import {
  FETCH_USER_FOR_SESSION_SUCCESS,
  USER_LOAD_FROM_SESSIONSTORAGE,
  USER_LOGOUT,
  SET_CURRENT_GROUP,
  SET_CURRENT_NETWORK,
  SET_IS_COORDINATION_GROUP,
  FEATURE_FLAG_UPDATE,
} from 'src/actions';

require('@babel/polyfill');

export class FeatureFlags {
  constructor() {
    this.context = { kind: 'user', key: 'unknown', anonymous: true };
    this.groups = {};
    this.thunks = {};

    this.reducer = this.reducer.bind(this);
  }

  onChange(key, thunk) {
    this.thunks[key] ||= [];
    this.thunks[key].push(thunk);
  }

  dispatchFlags(dispatch, getState) {
    const flags = this.client.allFlags();

    dispatch({
      type: FEATURE_FLAG_UPDATE,
      payload: {
        flags,
      },
    });

    Object.entries(flags).forEach(([key, value]) => {
      const thunks = this.thunks[key] || [];

      thunks.forEach((thunk) => {
        thunk(dispatch, getState, value);
      });
    });

    return flags;
  }

  init({ clientId }) {
    this.client = createClient(clientId, this.context, {
      useReport: true,
    });

    return (dispatch, getState) => new Promise((resolve) => {
      this.client.on('ready', () => {
        this.identify().then(() => {
          resolve(this.dispatchFlags(dispatch, getState));
        });
      });

      this.client.on('change', () => {
        this.dispatchFlags(dispatch, getState);
      });

      this.client.start();
    });
  }

  load(user) {
    this.groups = (user.groups || []).reduce((groups, group) => {
      const groupId = group?.group?.id;

      const packageIds = (group.licensing || []).reduce((pids, license) => {
        const packageId = license?.package?.id;
        if (!packageId) return pids;

        return [...pids, packageId];
      }, []);

      const features = Object.keys(group.feature_flags).sort();
      const role_keys = Object.values(group.roles).map((role) => role.role_key).sort();

      return {
        ...groups,
        [groupId]: {
          packageIds,
          features,
          role_keys,
        },
      };
    }, {});

    this.context = {
      ...this.context,
      key: user.id,
      firstName: user.first_name,
      lastName: user.last_name,
      name: user.full_name,
      email: user.email,
      avatar: user.avatar_url,
      anonymous: false,
      created: user.created_at,
    };
  }

  mapCurrentGroup() {
    const id = this.context.groupId;
    if (!id) return;

    this.context = {
      ...this.context,
      packageIds: this.groups[id]?.packageIds ?? [],
      features: this.groups[id]?.features ?? [],
      role_keys: this.groups[id]?.role_keys ?? [],
    };
  }

  async identify() {
    try {
      await this.client.identify(this.context); // eslint-disable-line
    } catch (e) {
      // ignore error
    }
  }

  reducer(state = {}, action) {
    switch (action.type) {
      case USER_LOAD_FROM_SESSIONSTORAGE:
        this.load(action.payload.user);
        this.mapCurrentGroup();

        this.identify();
        break;
      case FETCH_USER_FOR_SESSION_SUCCESS:
        this.load(action.payload.data.data);
        this.mapCurrentGroup();

        this.identify();
        break;
      case SET_CURRENT_GROUP:
        this.context = {
          ...this.context,
          groupId: action.groupId,
        };

        this.mapCurrentGroup();

        this.identify();
        break;
      case SET_CURRENT_NETWORK:
        this.context = {
          ...this.context,
          networkId: action.networkId,
        };

        this.identify();
        break;
      case SET_IS_COORDINATION_GROUP:
        this.context = {
          ...this.context,
          isCoordinationGroup: action.value,
        };

        this.identify();
        break;
      case USER_LOGOUT:
        this.context = { kind: 'user', anonymous: true };
        this.identify();
        break;
      case FEATURE_FLAG_UPDATE: {
        const flags = mapKeys(action.payload.flags, (value, key) => camelCase(key));

        return {
          ...state,
          ...flags,
        };
      }
      default:
        return state;
    }

    return state;
  }
}

export default new FeatureFlags();
